import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";

/* =========================
   MAIN COMPONENT
========================= */
export default function GreenSpaces() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  const [places, setPlaces] = useState([]);
  const [center, setCenter] = useState({
    lat: 19.0760,
    lng: 72.8777
  });

  /* =========================
     LOAD LEAFLET CSS SAFELY
  ========================= */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  /* =========================
     INIT MAP
  ========================= */
  useEffect(() => {
    if (mapInstance.current) return;

    mapInstance.current = L.map(mapRef.current).setView(
      [center.lat, center.lng],
      13
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors"
    }).addTo(mapInstance.current);

    markerRef.current = L.marker([center.lat, center.lng]).addTo(
      mapInstance.current
    );

    mapInstance.current.on("click", (e) => {
      const { lat, lng } = e.latlng;

      setCenter({ lat, lng });
      markerRef.current.setLatLng([lat, lng]);
      fetchPlaces(lat, lng);
    });

    fetchPlaces(center.lat, center.lng);
  }, []);

  /* =========================
     FETCH API
  ========================= */
  const fetchPlaces = async (lat, lng) => {
    try {
      const url = `https://comp-oriented.onrender.com/api/places/green?lat=${lat}&lng=${lng}&radius=5000&keyword=park`;
      const res = await fetch(url);
      const data = await res.json();

      console.log("API RESPONSE:", data);

      setPlaces(Array.isArray(data.results) ? data.results : []);
    } catch (err) {
      console.error("Fetch failed", err);
      setPlaces([]);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Green Space Recommendations</h1>
      <p style={styles.subtitle}>
        Click anywhere on the map to discover nearby green spaces 🌱
      </p>

      {/* MAP */}
      <div ref={mapRef} style={styles.map} />

      <p style={styles.coords}>
        Selected: {center.lat.toFixed(4)}, {center.lng.toFixed(4)}
      </p>

      {/* CARDS */}
      <div style={styles.grid}>
        {places.map((p, i) => (
          <div key={i} style={styles.card}>
            <h3>{p.name || "Unnamed Green Space"}</h3>
            {p.vicinity && (
              <p style={styles.muted}>{p.vicinity}</p>
            )}
            <span style={styles.tag}>Green Space</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================
   STYLES
========================= */
const styles = {
  page: {
    maxWidth: "1200px",
    margin: "auto",
    padding: "2rem",
    fontFamily: "Segoe UI, system-ui"
  },
  title: {
    color: "#ff6b2c",
    textAlign: "center",
    fontSize: "2.4rem"
  },
  subtitle: {
    textAlign: "center",
    color: "#666",
    marginBottom: "1.5rem"
  },
  map: {
    width: "100%",
    height: "420px",
    borderRadius: "16px",
    border: "1px solid #ddd",
    marginBottom: "1rem"
  },
  coords: {
    textAlign: "center",
    fontWeight: "600",
    marginBottom: "1.5rem"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "1.5rem"
  },
  card: {
    background: "white",
    padding: "1.4rem",
    borderRadius: "16px",
    border: "1px solid #eee",
    boxShadow: "0 10px 30px rgba(0,0,0,0.05)"
  },
  muted: {
    color: "#666",
    fontSize: "0.85rem"
  },
  tag: {
    display: "inline-block",
    marginTop: "0.8rem",
    background: "#e9f8ef",
    color: "#2ecc71",
    padding: "0.3rem 0.7rem",
    borderRadius: "12px",
    fontSize: "0.75rem",
    fontWeight: "600"
  }
};
